/**
 * Per-conversation dismissed-upsell persistence.
 *
 * Dismissal stays client-side on purpose: clicking the X must NOT trigger a
 * Scout turn (otherwise the model would respond with an acknowledgement). The
 * model's "call present_scout_pro_upsell at most once per conversation" rule
 * already prevents re-pitching via its tool-call history, so we don't need a
 * server-visible signal — only a UI signal that survives page refresh.
 *
 * Keyed on conversationId so dismissal really is per-conversation: a new
 * conversation gets a fresh upsell.
 */

const KEY_PREFIX = "scout-pro-upsell-dismissed:";

function isStorageAvailable(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function storageKey(conversationId: string): string {
  return `${KEY_PREFIX}${conversationId}`;
}

export function readDismissedScoutProUpsells(
  conversationId: string | undefined
): Record<string, "dismissed"> {
  if (!conversationId || !isStorageAvailable()) return {};
  try {
    const raw = window.localStorage.getItem(storageKey(conversationId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    const out: Record<string, "dismissed"> = {};
    for (const key of Object.keys(parsed)) {
      if (typeof key === "string" && parsed[key] === "dismissed") {
        out[key] = "dismissed";
      }
    }
    return out;
  } catch {
    return {};
  }
}

export function writeDismissedScoutProUpsells(
  conversationId: string | undefined,
  dismissed: Record<string, "dismissed">
): void {
  if (!conversationId || !isStorageAvailable()) return;
  try {
    if (Object.keys(dismissed).length === 0) {
      window.localStorage.removeItem(storageKey(conversationId));
      return;
    }
    window.localStorage.setItem(storageKey(conversationId), JSON.stringify(dismissed));
  } catch {
    // Quota exceeded or storage disabled — non-fatal, the card just stays
    // hidden until reload.
  }
}
